// 批量新建地图（CopyDrawer "批量新建" 模式）所用的纯函数。
//
// 模型：用户填写带占位符的模板（map_id / name / JSON 字段中任意 string），
// 配合一个数字序列。每个 n 渲染一份 MapUpsertInput，串行落库即可。
//
// 占位符：{n} / {n_pad2}（两位 pad）/ {n_cn}（中文小写 1..99）/ {index}（克隆顺
// 序，从 1 起），支持整数偏移 {n+K} / {n-K}，也兼容 {K+n} / {K+index}。
// 其他 {...} 原样保留。

export interface RenderContext {
  /** 当前序列值。 */
  n: number;
  /** 克隆顺序，从 1 起。 */
  index: number;
}

const CN_DIGITS = ["零", "一", "二", "三", "四", "五", "六", "七", "八", "九"];

function toChineseDigits(n: number): string {
  if (!Number.isFinite(n) || n < 0 || n > 99 || !Number.isInteger(n)) {
    return String(n);
  }
  if (n < 10) return CN_DIGITS[n];
  if (n === 10) return "十";
  if (n < 20) return "十" + CN_DIGITS[n - 10];
  const tens = Math.floor(n / 10);
  const ones = n % 10;
  if (ones === 0) return CN_DIGITS[tens] + "十";
  return CN_DIGITS[tens] + "十" + CN_DIGITS[ones];
}

const PLACEHOLDER_RE = /\{([^{}]+)\}/g;
const BASE_PLACEHOLDER_RE = /^(n_pad2|n_cn|n|index)(?:([+-])(\d+))?$/;
const CONSTANT_PLACEHOLDER_RE = /^(-?\d+)([+-])(n|index)$/;

const NUMERIC_JSON_KEYS = new Set([
  "index",
  "newIndex",
  "npcX",
  "npcY",
  "npcRange",
  "npcTalkWaitTimeout",
  "mapWaitTimeout",
  "waitSec",
  "delay",
  "x",
  "y",
  "range",
  "count",
  "moveTimeout",
  "npcMoveTimeout",
  "npcMoveRetryInterval",
  "maxEnterRetry",
  "routeFailCooldown",
]);

function renderBasePlaceholder(
  key: string,
  offset: number,
  ctx: RenderContext,
): string {
  switch (key) {
    case "n":
      return String(ctx.n + offset);
    case "n_pad2":
      return String(ctx.n + offset).padStart(2, "0");
    case "n_cn":
      return toChineseDigits(ctx.n + offset);
    case "index":
      return String(ctx.index + offset);
    default:
      return "";
  }
}

function resolvePlaceholder(rawExpression: string, ctx: RenderContext): string | null {
  const expression = rawExpression.replace(/\s+/g, "");

  const baseMatch = expression.match(BASE_PLACEHOLDER_RE);
  if (baseMatch) {
    const [, key, sign, offsetStr] = baseMatch;
    const offset =
      sign && offsetStr ? (sign === "-" ? -1 : 1) * Number(offsetStr) : 0;
    return renderBasePlaceholder(key, offset, ctx);
  }

  const constantMatch = expression.match(CONSTANT_PLACEHOLDER_RE);
  if (constantMatch) {
    const [, baseStr, sign, key] = constantMatch;
    const base = Number(baseStr);
    const value = key === "index" ? ctx.index : ctx.n;
    return String(sign === "-" ? base - value : base + value);
  }

  return null;
}

/** 在字符串里把占位符渲染掉。不认识的 {...} 原样保留。 */
export function renderTemplate(template: string, ctx: RenderContext): string {
  return template.replace(PLACEHOLDER_RE, (full: string, expression: string) => {
    const rendered = resolvePlaceholder(expression, ctx);
    return rendered ?? full;
  });
}

function shouldCoerceRenderedNumber(
  key: string | undefined,
  original: string,
  rendered: string,
): boolean {
  if (!key || !NUMERIC_JSON_KEYS.has(key)) return false;
  if (original === rendered) return false;
  if (!/^\{[^{}]+\}$/.test(original.trim())) return false;
  return /^-?\d+(?:\.\d+)?$/.test(rendered);
}

/**
 * 递归把 JSON-like 值中所有 string 叶子通过 renderTemplate 渲染一次。
 * 不动 object key、不动数据结构、不动非 string 类型。
 */
export function deepRenderTemplates(
  value: unknown,
  ctx: RenderContext,
  key?: string,
): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === "string") {
    const rendered = renderTemplate(value, ctx);
    if (shouldCoerceRenderedNumber(key, value, rendered)) {
      return Number(rendered);
    }
    return rendered;
  }
  if (Array.isArray(value)) {
    return value.map((item) => deepRenderTemplates(item, ctx));
  }
  if (typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = deepRenderTemplates(v, ctx, k);
    }
    return out;
  }
  return value;
}

/**
 * 解析"序列输入"——支持 "2-9" 范围、"2,3,5,7" 列表，或两者混合 "2-4,6,8-10"。
 * 失败返回空数组。重复值会被去掉，结果按出现顺序保留。
 */
export function parseSequenceInput(raw: string): number[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const out: number[] = [];
  const seen = new Set<number>();
  for (const token of trimmed.split(/[,\s]+/)) {
    if (!token) continue;
    const rangeMatch = token.match(/^(-?\d+)-(-?\d+)$/);
    if (rangeMatch) {
      const a = Number(rangeMatch[1]);
      const b = Number(rangeMatch[2]);
      if (!Number.isInteger(a) || !Number.isInteger(b)) return [];
      const step = a <= b ? 1 : -1;
      for (let i = a; step > 0 ? i <= b : i >= b; i += step) {
        if (!seen.has(i)) {
          seen.add(i);
          out.push(i);
        }
      }
      continue;
    }
    const single = Number(token);
    if (!Number.isInteger(single)) return [];
    if (!seen.has(single)) {
      seen.add(single);
      out.push(single);
    }
  }
  return out;
}
